import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, CalendarDays, ChevronDown, ChevronRight, ChevronLeft, Home, Sparkles } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import { trackPhoneClick, trackBookingClick } from "@/lib/gtm";
import { TREATMENT_BOOKING_URLS } from "@shared/bookingLinks";

const LOGO = "https://d2xsxph8kpxj0f.cloudfront.net/310519663381010537/AX6sNuigosh2pncBHVxRxQ/logo_optimized_1ee89f17.webp";
const BOOKING = "https://medsparecord.com/book?step=0";

// ── Organized treatment menu ────────────────────────────────────────
interface NavCategory {
  heading: string;
  overview?: string;
  items: { label: string; href: string }[];
}

const NAV_CATEGORIES: NavCategory[] = [
  {
    heading: "Face & Skin",
    overview: "/face-skin-rejuvenation",
    items: [
      { label: "NEW · XERF Structural Skin Tightening", href: "/xerf-skin-tightening" },
      { label: "Non-Surgical Face Lift", href: "/non-surgical-face-lift" },
      { label: "RF Microneedling + PRP",    href: "/rf-microneedling" },
      { label: "Microneedling Options", href: "/microneedling" },
      { label: "CO₂ Fractional Laser",      href: "/fractional-co2" },
      { label: "DuoVive™ 1927 Laser",       href: "/duovive" },
      { label: "IPL Photofacial",      href: "/dpl-photofacial" },
      { label: "Pico Laser",                href: "/pico-laser" },
      { label: "AquaFacial",                 href: "/aquafacial" },
      { label: "Skin Analysis",              href: "/skin-analysis" },
      { label: "Stretch Mark Treatment",     href: "/stretch-mark" },
      { label: "Focused Skin Tightening (HIFU)", href: "/hifu" },
      { label: "Skin Laxity & Crepiness",    href: "/skin-laxity" },
      { label: "Skin Conditions",            href: "/face-skin-conditions" },
    ],
  },
  {
    heading: "Injectables",
    overview: "/injectables",
    items: [
      { label: "Botox & Dysport",            href: "/botox" },
      { label: "Dermal Fillers",             href: "/dermal-fillers" },
      { label: "Facial Balancing",           href: "/facial-balancing" },
      { label: "Lip Fillers",                href: "/lip-fillers" },
      { label: "Kybella® (Double Chin)",     href: "/kybella" },
      { label: "PDO Thread Lift",            href: "/pdo-threads" },
      { label: "Sculptra & Radiesse",        href: "/biostimulators" },
      { label: "Chemical Peels",             href: "/chemical-peels" },
      { label: "Dark Circles & Eye Area",     href: "/dark-circles" },
      { label: "Facial Volume & Aging",       href: "/facial-volume" },
    ],
  },
  {
    heading: "Body Contouring",
    overview: "/body-contouring",
    items: [
      { label: "Body Sculpting Overview",    href: "/body-sculpting" },
      { label: "RenaSculpt (HIFEM)",         href: "/renasculpt" },
      { label: "Cryolipolysis Fat Freezing", href: "/cryolipolysis" },
      { label: "1060nm Diode Lipolysis",     href: "/diode-laser-lipolysis" },
      { label: "Stubborn Fat Solutions",     href: "/stubborn-body-fat" },
    ],
  },
  {
    heading: "Hair",
    items: [
      { label: "PRP Hair Restoration",       href: "/hair-growth" },
      { label: "LLLT Laser Hair Growth",     href: "/lllt-hair-restoration" },
      { label: "Laser Hair Removal",         href: "/areslite-hair-removal" },
    ],
  },
  {
    heading: "Intimate Wellness",
    overview: "/intimate-wellness",
    items: [
      { label: "Vaginal Rejuvenation",       href: "/vaginal-rejuvenation" },
      { label: "O-Shot & P-Shot (PRP)",      href: "/prp-sexual-wellness" },
      { label: "Pelvic Floor (HIFEM)",       href: "/pelvic-floor" },
    ],
  },
  {
    heading: "Pain & Recovery",
    overview: "/pain-regenerative-medicine",
    items: [
      { label: "Shockwave Therapy",          href: "/pain-regenerative-medicine" },
    ],
  },
  {
    heading: "Weight Loss & Wellness",
    items: [
      { label: "Medical Weight Loss",        href: "/weight-loss" },
      { label: "Hormone Optimization",       href: "/hormone-optimization" },
      { label: "Peptide Therapy",            href: "/peptide-therapy" },
      { label: "Functional Lab Testing",     href: "/functional-lab-testing" },
    ],
  },
];

const ALL_TREATMENT_HREFS = NAV_CATEGORIES.flatMap(c =>
  [c.overview, ...c.items.map(i => i.href)].filter(Boolean) as string[]
);

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [location] = useLocation();
  // Paid XERF visitors: header Book links open the free XERF consult, not the generic hub.
  const bookingHref = location.split("?")[0] === "/xerf-skin-tightening" ? TREATMENT_BOOKING_URLS.xerf : BOOKING;
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenCategory(null);
  }, [location]);

  const isActive = (href: string) => location === href;
  const isTreatmentActive = ALL_TREATMENT_HREFS.includes(location);

  const accent = "#5B7A9E";
  const charcoal = "#8B6B6B";
  const ivory = "#F6F1EA";
  const border = "#e8ddd0";

  const navLabelStyle = {
    fontFamily: "'Lato', sans-serif",
    fontSize: "0.72rem",
    fontWeight: 700 as const,
    letterSpacing: "0.14em",
    textTransform: "uppercase" as const,
    textDecoration: "none",
    whiteSpace: "nowrap" as const,
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 1.2,
    transition: "color 0.2s",
  };

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        backgroundColor: "rgba(250,247,242,0.97)",
        borderBottom: "1px solid var(--divider)",
        backdropFilter: "blur(8px)",
        overflow: "visible",
      }}
    >
      <div className="container">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 80, gap: "0.5rem" }}>

          {/* Logo — always links home */}
          <Link href="/" style={{ flexShrink: 0 }}>
            <img src={LOGO} alt="Lakewood Med Spa" className="nav-logo" width={200} height={187} style={{ height: 64, width: "auto", objectFit: "contain", display: "block" }} loading="lazy" />
          </Link>

          {/* Desktop Nav */}
          <nav style={{ display: "flex", alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap", gap: "clamp(0.85rem, 1.35vw, 1.5rem)" }} className="desktop-nav">

            {/* Treatments Mega Dropdown */}
            <div ref={dropdownRef} style={{ position: "relative" }}>
              <button
                onClick={() => setDropdownOpen(o => !o)}
                style={{
                  ...navLabelStyle,
                  display: "flex", alignItems: "center", gap: 4,
                  color: isTreatmentActive ? accent : charcoal,
                  background: "none", border: "none", cursor: "pointer", padding: 0,
                }}
                onMouseOver={e => { if (!isTreatmentActive) (e.currentTarget as HTMLElement).style.color = accent; }}
                onMouseOut={e => { if (!isTreatmentActive) (e.currentTarget as HTMLElement).style.color = charcoal; }}
              >
                Treatments
                <ChevronDown size={12} style={{ transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
              </button>

              {dropdownOpen && (
                <div style={{
                  position: "fixed", top: "auto", left: "50%", transform: "translateX(-50%)",
                  marginTop: 0,
                  background: "#fff", border: `1px solid ${border}`, borderRadius: 14,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.12)", padding: "1rem 0", zIndex: 9999,
                  display: "grid", gridTemplateColumns: "repeat(3, minmax(200px, 1fr))", gap: 0,
                  minWidth: 640, maxWidth: "95vw", maxHeight: "75vh", overflowY: "auto",
                }}>
                  {NAV_CATEGORIES.map((cat) => (
                    <div key={cat.heading} style={{ padding: "0.5rem 1.25rem", borderRight: `1px solid ${border}` }}>
                      {cat.overview ? (
                        <Link href={cat.overview}>
                          <div
                            onClick={() => setDropdownOpen(false)}
                            style={{
                              fontFamily: "'Lato',sans-serif", fontSize: "0.68rem", fontWeight: 800,
                              letterSpacing: "0.14em", textTransform: "uppercase", color: accent,
                              marginBottom: "0.5rem", cursor: "pointer", paddingBottom: "0.35rem",
                              borderBottom: `1.5px solid ${border}`,
                            }}
                          >
                            {cat.heading}
                          </div>
                        </Link>
                      ) : (
                        <div style={{
                          fontFamily: "'Lato',sans-serif", fontSize: "0.68rem", fontWeight: 800,
                          letterSpacing: "0.14em", textTransform: "uppercase", color: accent,
                          marginBottom: "0.5rem", paddingBottom: "0.35rem",
                          borderBottom: `1.5px solid ${border}`,
                        }}>
                          {cat.heading}
                        </div>
                      )}
                      {cat.items.map((item) => (
                        <Link key={item.href + item.label} href={item.href}>
                          <div
                            onClick={() => setDropdownOpen(false)}
                            style={{
                              fontFamily: "'Lato',sans-serif", fontSize: "0.78rem", fontWeight: 500,
                              color: isActive(item.href) ? accent : "#4a5a50", padding: "0.4rem 0",
                              cursor: "pointer", transition: "color 0.15s",
                            }}
                            onMouseOver={e => (e.currentTarget.style.color = accent)}
                            onMouseOut={e => (e.currentTarget.style.color = isActive(item.href) ? accent : "#4a5a50")}
                          >
                            {item.label}
                          </div>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link href="/membership">
              <span style={{ ...navLabelStyle, color: isActive("/membership") ? accent : charcoal }}
                onMouseOver={e => (e.currentTarget.style.color = accent)}
                onMouseOut={e => (e.currentTarget.style.color = isActive("/membership") ? accent : charcoal)}
              >
                Membership
              </span>
            </Link>

            <Link href="/specials">
              <span style={{ ...navLabelStyle, color: isActive("/specials") ? accent : charcoal }}
                onMouseOver={e => (e.currentTarget.style.color = accent)}
                onMouseOut={e => (e.currentTarget.style.color = isActive("/specials") ? accent : charcoal)}
              >
                New & Offers
              </span>
            </Link>

            <Link href="/technology-comparison">
              <span style={{ ...navLabelStyle, color: isActive("/technology-comparison") ? accent : charcoal }}
                onMouseOver={e => (e.currentTarget.style.color = accent)}
                onMouseOut={e => (e.currentTarget.style.color = isActive("/technology-comparison") ? accent : charcoal)}
              >
                Technology
              </span>
            </Link>

            <Link href="/blog">
              <span style={{ ...navLabelStyle, color: isActive("/blog") ? accent : charcoal }}
                onMouseOver={e => (e.currentTarget.style.color = accent)}
                onMouseOut={e => (e.currentTarget.style.color = isActive("/blog") ? accent : charcoal)}
              >
                Journal
              </span>
            </Link>

            <Link href="/ai-skin-analysis">
              <span
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "0.35rem 0.85rem",
                  borderRadius: 20,
                  border: `1.5px solid ${isActive("/ai-skin-analysis") ? accent : accent}`,
                  background: isActive("/ai-skin-analysis") ? accent : "rgba(91,122,158,0.08)",
                  color: isActive("/ai-skin-analysis") ? "#fff" : accent,
                  fontFamily: "'Lato',sans-serif",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  transition: "background 0.18s, color 0.18s",
                  whiteSpace: "nowrap",
                }}
                onMouseOver={e => {
                  if (!isActive("/ai-skin-analysis")) {
                    (e.currentTarget as HTMLElement).style.background = accent;
                    (e.currentTarget as HTMLElement).style.color = "#fff";
                  }
                }}
                onMouseOut={e => {
                  if (!isActive("/ai-skin-analysis")) {
                    (e.currentTarget as HTMLElement).style.background = "rgba(91,122,158,0.08)";
                    (e.currentTarget as HTMLElement).style.color = accent;
                  }
                }}
              >
                <Sparkles size={11} />
                Free AI Skin Analysis
              </span>
            </Link>

            <Link href="/contact">
              <span style={{ ...navLabelStyle, color: isActive("/contact") ? accent : charcoal }}
                onMouseOver={e => (e.currentTarget.style.color = accent)}
                onMouseOut={e => (e.currentTarget.style.color = isActive("/contact") ? accent : charcoal)}
              >
                Contact
              </span>
            </Link>
          </nav>

          {/* Desktop Search */}
          <div className="desktop-nav" style={{ flex: "0 0 auto" }}>
            <SearchBar className="w-56" />
          </div>
          {/* Desktop CTA */}
          <div className="desktop-nav">
            <a href={bookingHref} target="_blank" rel="noopener noreferrer" className="btn-primary"
              onClick={() => trackBookingClick("navbar")}
              style={{ padding: "0.6rem 1.25rem", fontSize: "0.72rem" }}>
              <CalendarDays size={13} /> Book Online
            </a>
          </div>

          {/* Mobile hamburger */}
          <button className="mobile-nav" onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            style={{ background: "none", border: "none", padding: "0.5rem", color: "var(--charcoal)" }}>
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* ── Mobile Menu ──────────────────────────────────────────── */}
      {mobileOpen && (
        <div style={{ backgroundColor: ivory, borderTop: "1px solid var(--divider)", padding: "0 0 calc(1.25rem + env(safe-area-inset-bottom))", maxHeight: "85vh", overflowY: "auto", WebkitOverflowScrolling: "touch" } as React.CSSProperties}>
          {/* Mobile Search */}
          <div style={{ padding: "0.75rem 1.25rem", borderBottom: `1px solid ${border}` }}>
            <SearchBar onClose={() => setMobileOpen(false)} />
          </div>
          <nav style={{ display: "flex", flexDirection: "column" }}>

            {/* HOME — first item */}
            <Link href="/">
              <div onClick={() => setMobileOpen(false)} style={{
                display: "flex", alignItems: "center", gap: 10,
                fontFamily: "'Lato',sans-serif", fontSize: "0.82rem", fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase",
                color: isActive("/") ? accent : charcoal,
                padding: "0.85rem 1.25rem", borderBottom: `1px solid ${border}`,
                background: isActive("/") ? "#fdf0e8" : "transparent",
              }}>
                <Home size={15} />
                Home
              </div>
            </Link>

            {/* Treatment categories as accordion */}
            {NAV_CATEGORIES.map((cat) => {
              const isOpen = openCategory === cat.heading;
              const catActive = cat.items.some(i => isActive(i.href)) || (cat.overview && isActive(cat.overview));
              return (
                <div key={cat.heading}>
                  <button
                    onClick={() => setOpenCategory(isOpen ? null : cat.heading)}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%",
                      fontFamily: "'Lato',sans-serif", fontSize: "0.82rem", fontWeight: 700,
                      letterSpacing: "0.1em", textTransform: "uppercase",
                      color: catActive ? accent : charcoal,
                      padding: "0.85rem 1.25rem", background: "none", border: "none",
                      borderBottom: `1px solid ${border}`, cursor: "pointer", textAlign: "left",
                    }}
                  >
                    {cat.heading}
                    <ChevronRight size={13} style={{
                      transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform 0.2s", color: accent,
                    }} />
                  </button>

                  {isOpen && (
                    <div style={{ background: "#fdf7f2", borderBottom: `1px solid ${border}` }}>
                      {/* Back button to collapse submenu */}
                      <button
                        onClick={() => setOpenCategory(null)}
                        style={{
                          display: "flex", alignItems: "center", gap: 6, width: "100%",
                          fontFamily: "'Lato',sans-serif", fontSize: "0.72rem", fontWeight: 700,
                          letterSpacing: "0.08em", textTransform: "uppercase",
                          color: "#8a7e72", padding: "0.6rem 1.25rem",
                          background: "#f0e8de", border: "none", borderBottom: `1px solid ${border}`,
                          cursor: "pointer",
                        }}
                      >
                        <ChevronLeft size={12} />
                        Back to Menu
                      </button>
                      {/* Overview link if exists */}
                      {cat.overview && (
                        <Link href={cat.overview}>
                          <div onClick={() => setMobileOpen(false)} style={{
                            fontFamily: "'Lato',sans-serif", fontSize: "0.75rem", fontWeight: 700,
                            letterSpacing: "0.08em", color: accent, padding: "0.7rem 1.5rem",
                            borderBottom: `1px solid ${border}`,
                          }}>
                            View All {cat.heading} →
                          </div>
                        </Link>
                      )}
                      {cat.items.map((item) => (
                        <Link key={item.href + item.label} href={item.href}>
                          <div onClick={() => setMobileOpen(false)} style={{
                            fontFamily: "'Lato',sans-serif", fontSize: "0.8rem", fontWeight: 500,
                            color: isActive(item.href) ? accent : "#4a5a50",
                            padding: "0.7rem 1.5rem 0.7rem 2rem",
                            borderBottom: `1px solid ${border}`,
                            background: isActive(item.href) ? "#fdf0e8" : "transparent",
                          }}>
                            {item.label}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Membership */}
            <Link href="/membership">
              <div onClick={() => setMobileOpen(false)} style={{
                fontFamily: "'Lato',sans-serif", fontSize: "0.82rem", fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase",
                color: isActive("/membership") ? accent : charcoal,
                padding: "0.85rem 1.25rem", borderBottom: `1px solid ${border}`,
              }}>
                Membership & Packages
              </div>
            </Link>

            <Link href="/specials">
              <div onClick={() => setMobileOpen(false)} style={{
                fontFamily: "'Lato',sans-serif", fontSize: "0.82rem", fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase",
                color: isActive("/specials") ? accent : charcoal,
                padding: "0.85rem 1.25rem", borderBottom: `1px solid ${border}`,
              }}>
                New & Offers
              </div>
            </Link>

            {/* Technology */}
            <Link href="/technology-comparison">
              <div onClick={() => setMobileOpen(false)} style={{
                fontFamily: "'Lato',sans-serif", fontSize: "0.82rem", fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase",
                color: isActive("/technology-comparison") ? accent : charcoal,
                padding: "0.85rem 1.25rem", borderBottom: `1px solid ${border}`,
              }}>
                Technology
              </div>
            </Link>

            {/* Journal */}
            <Link href="/blog">
              <div onClick={() => setMobileOpen(false)} style={{
                fontFamily: "'Lato',sans-serif", fontSize: "0.82rem", fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase",
                color: isActive("/blog") ? accent : charcoal,
                padding: "0.85rem 1.25rem", borderBottom: `1px solid ${border}`,
              }}>
                Journal
              </div>
            </Link>

            {/* Contact */}
            <a href="tel:2142476793" onClick={() => trackPhoneClick("navbar_mobile")} style={{
              fontFamily: "'Lato',sans-serif", fontSize: "0.82rem", fontWeight: 700,
              letterSpacing: "0.1em", textTransform: "uppercase", color: charcoal,
              padding: "0.85rem 1.25rem", borderBottom: `1px solid ${border}`,
              display: "block", textDecoration: "none",
            }}>
              Call / Text (214) 247-6793
            </a>
          </nav>

          {/* Book + AI CTAs */}
          <div style={{ padding: "1rem 1.25rem 0", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <a href={bookingHref} target="_blank" rel="noopener noreferrer" className="btn-primary"
              style={{ width: "100%", justifyContent: "center" }} onClick={() => { setMobileOpen(false); trackBookingClick("navbar_mobile"); }}>
              <CalendarDays size={14} /> Book Online
            </a>
            <Link href="/ai-skin-analysis">
              <div onClick={() => setMobileOpen(false)} style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                width: "100%", padding: "0.7rem 1rem",
                borderRadius: 8, border: `1.5px solid ${accent}`,
                background: "rgba(91,122,158,0.08)",
                color: accent, fontFamily: "'Lato',sans-serif",
                fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.05em",
                cursor: "pointer", boxSizing: "border-box",
              }}>
                <Sparkles size={14} />
                Free AI Skin Analysis
              </div>
            </Link>
          </div>
        </div>
      )}

      <style>{`
        @media (min-width: 1100px) {
          .desktop-nav { display: flex !important; }
          .mobile-nav { display: none !important; }
        }
        @media (max-width: 1099px) {
          .desktop-nav { display: none !important; }
          .mobile-nav { display: flex !important; }
          .nav-logo { height: 52px !important; }
        }
        @media (max-width: 380px) {
          .nav-logo { height: 44px !important; }
        }
      `}</style>
    </header>
  );
}
